<script lang="ts">
  // @ts-expect-error
  import Svelecte from "svelecte";
  import * as c from "country-region-data";
  import { _, locale } from "svelte-i18n";

  import Steps from "./Steps.svelte";
  import TrialLayout from "./TrialLayout.svelte";
  import {
    getPartnerEmailFromNavigator,
    getOrganizationIdFromNavigator,
    getLangFromNavigator,
    getRefererFromNavigator,
    RegistrationInfoRequestBody,
    getPricingPlanFromNavigator,
  } from "./types";
  import { register } from "./api";
  import { Analytics, logEvent } from "firebase/analytics";
  import { PricingPlan } from "../functions/src/types";

  interface SvelecteOption {
    label: string;
    value: string;
  }

  export let analytics: Analytics;

  logEvent(analytics, "registration_start", { name: "registration_start" });

  let options = c.allCountries.map(([label, value]) => ({ value, label }));
  let countrySelection: SvelecteOption | null = null;

  $: langOptions = [
    { label: $_("register.form.lang_select.english"), value: "en" },
    { label: $_("register.form.lang_select.czech"), value: "cs" },
    { label: $_("register.form.lang_select.slovak"), value: "sk" },
    { label: $_("register.form.lang_select.german"), value: "de" },
    { label: $_("register.form.lang_select.hungarian"), value: "hu" },
    { label: $_("register.form.lang_select.italian"), value: "it" },
    { label: $_("register.form.lang_select.spanish"), value: "es" },
  ];

  let langSelection: SvelecteOption | null = null;
  let langValue = getLangFromNavigator();
  const organizationId = getOrganizationIdFromNavigator();
  const partnerEmail = getPartnerEmailFromNavigator();
  const pricingPlan = getPricingPlanFromNavigator();

  // TODO: password strong meter
  // TODO: eslint
  const recaptchaSiteKey = "6LeXOjIgAAAAANA779cXkF6Bi730IgTDHwMlBCIM";

  let registerForm: HTMLFormElement;
  let isFormSending = false;
  $: registrationFinished = false;

  let referer = getRefererFromNavigator();

  async function handleRegister() {
    const inputs = [
      validateCompany(document.getElementById("company") as HTMLInputElement),
      validateCountry(countrySelection),
      validateFullName(document.getElementById("fullName") as HTMLInputElement),
      validateLang(langSelection),
      validateEmail(document.getElementById("email") as HTMLInputElement),
      validatePhone(document.getElementById("phone") as HTMLInputElement),
      validateAgreement(),
    ];
    const isValid = inputs.reduce((acc, curr) => acc && curr, true);
    if (!isValid) {
      return;
    }

    const data = new FormData(registerForm);
    const registerInfo: RegistrationInfoRequestBody = {
      company: data.get("company")!.toString(),
      fullName: data.get("fullName")!.toString(),
      country: countrySelection?.value ?? "",
      email: data.get("email")!.toString(),
      phone: data.get("phone")!.toString(),
      intent: data.get("intent")!.toString(),
      lang: langValue as any,
      captchaToken: await window.grecaptcha.execute(recaptchaSiteKey, {
        action: "register",
      }),
      pricingPlan,
      referer,
      organizationId: organizationId || "",
      partnerEmail: partnerEmail || "",
    };

    isFormSending = true;
    register(registerInfo)
      .then((r) => {
        let errorText = $_("register.form.error.general");
        if (r.error || !r.success) {
          switch (r.error?.type) {
            case "AlreadyUsedEmail":
              errorText = $_("register.form.error.already_used_email");
              break;
            case "CaptchaInvalid":
              errorText = $_("register.form.error.captcha");
              break;
          }

          const errContainer = document.getElementById(
            "form-error"
          ) as HTMLElement;
          errContainer.textContent = errorText;
          errContainer.style.display = "block";
          return;
        }
        registrationFinished = true;

        // Odeslání události, že parent se může posunout k vrchnímu okraji.
        window.parent.postMessage(
          {
            eventName: "scrollTop",
            details: {},
          },
          "https://www.kiwisignage.com"
        );
        window.parent.postMessage(
          {
            eventName: "registerSubmit",
            details: {},
          },
          "https://www.kiwisignage.com"
        );

        logEvent(analytics, "registration_sent", { name: "registration_sent" });
      })
      .catch((err) => {
        console.error(err);
        logEvent(analytics, "registration_error", {
          name: "registration_sending",
          ...err,
        });
      })
      .finally(() => {
        isFormSending = false;
      });
  }

  function validateValue(
    validators: (() => boolean)[],
    errorCotainer: HTMLElement,
    errorMessages: string[]
  ): boolean {
    for (const [i, v] of validators.entries()) {
      if (!v()) {
        errorCotainer.textContent = errorMessages[i];
        errorCotainer.style.display = "block";
        return false;
      }
    }
    errorCotainer.textContent = "";
    errorCotainer.style.display = "none";
    return true;
  }

  function validateCompany(input: HTMLInputElement) {
    const { value } = input;
    const validator1 = (): boolean => {
      return !!value;
    };
    const errorContainer = document.getElementById(
      "company-error"
    ) as HTMLElement;
    return validateValue([validator1], errorContainer, [
      $_("register.form.error.empty_company"),
    ]);
  }

  function companyChanged(e: Event) {
    validateCompany(e.target as HTMLInputElement);
  }

  function validateCountry(detail: SvelecteOption | null): boolean {
    return validateValue(
      [() => !!detail && !!detail?.value],
      document.getElementById("country-error")!,
      [$_("register.form.error.empty_field")]
    );
  }

  function countryChanged(e: CustomEvent) {
    validateCountry(e.detail);
  }

  function validateLang(detail: SvelecteOption | null): boolean {
    return validateValue(
      [() => !!detail && !!detail?.label && !!detail?.value],
      document.getElementById("lang-error")!,
      [$_("register.form.error.empty_field")]
    );
  }

  function langChanged(e: CustomEvent) {
    validateLang(e.detail);
    if (e.detail && e.detail.value) {
      locale.set(e.detail.value);
    }
  }

  function validateFullName(input: HTMLInputElement) {
    const { value } = input;
    const validator1 = (): boolean => {
      return value.length > 2;
    };

    const validator2 = (): boolean => {
      const regex = /.+\s.+/g;
      const m = regex.exec(value);
      return m !== null;
    };
    const errorContainer = document.getElementById(
      "fullName-error"
    ) as HTMLElement;
    return validateValue([validator1, validator2], errorContainer, [
      $_("register.form.error.too_short_name"),
      $_("register.form.error.name_with_space"),
    ]);
  }

  function fullNameChanged(e: Event) {
    validateFullName(e.target as HTMLInputElement);
  }

  function validateEmail(input: HTMLInputElement) {
    const { value } = input;
    const validator1 = (): boolean => {
      const regex =
        /^[_A-Za-z0-9-\\+]+(\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9]+)*(\.[A-Za-z]{2,})$/g;
      const m = regex.exec(value);
      return m !== null;
    };
    const errorContainer = document.getElementById(
      "email-error"
    ) as HTMLElement;
    return validateValue([validator1], errorContainer, [
      $_("register.form.error.invalid_email"),
    ]);
  }

  function emailChanged(e: Event) {
    validateEmail(e.target as HTMLInputElement);
  }

  function validatePhone(input: HTMLInputElement) {
    const { value } = input;
    const validator1 = (): boolean => {
      return value.length >= 9;
    };
    const errorContainer = document.getElementById(
      "phone-error"
    ) as HTMLElement;
    return validateValue([validator1], errorContainer, [
      $_("register.form.error.invalid_phone"),
    ]);
  }

  function phoneChanged(e: Event) {
    validatePhone(e.target as HTMLInputElement);
  }

  function validateAgreement() {
    const { checked } = document.getElementById(
      "agreement"
    ) as HTMLInputElement;
    const validator1 = (): boolean => {
      return checked;
    };

    const formError = document.querySelector("#form-error") as HTMLElement;
    const isValid = validateValue([validator1], formError, [
      $_("register.form.error.agreement"),
    ]);
    const agreement = document.getElementById("agreement") as HTMLInputElement;

    if (!isValid) {
      agreement.style.outline = "3px solid #ff5c4b";
      agreement.style.outlineOffset = "3px";
      return false;
    }

    agreement.style.outline = "";
    agreement.style.outlineOffset = "0";

    return true;
  }

  function agreementChanged(e: Event) {
    validateAgreement();
  }
</script>

<svelte:head>
  <script
    src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
  ></script>
</svelte:head>

{#if !registrationFinished}
  <TrialLayout {pricingPlan}>
    <div class="form-wrapper">
      <form
        id="register-form"
        bind:this={registerForm}
        on:submit|preventDefault={handleRegister}
      >
        <div class="form-row">
          <div class="input-wrapper">
            <label for="company">{$_("register.form.company")} *</label>
            <input
              type="text"
              id="company"
              name="company"
              placeholder=" "
              on:input={companyChanged}
              on:change={companyChanged}
              aria-describedby="error-container"
            />
            <span id="company-error" class="error-container"
              >{$_("register.form.error.empty_field")}</span
            >
          </div>

          <div class="input-wrapper">
            <label for="country">{$_("register.form.country")} *</label>
            <Svelecte
              class="svelecte-control svelecte-control2"
              {options}
              inputId="country"
              bind:readSelection={countrySelection}
              placeholder={$_("register.form.country_placeholder")}
              clearable
              on:input={countryChanged}
              on:change={countryChanged}
            />
            <span id="country-error" class="error-container"
              >{$_("register.form.error.empty_field")}</span
            >
          </div>
        </div>

        <div class="form-row">
          <div class="input-wrapper">
            <label for="fullName">{$_("register.form.name")} *</label>
            <input
              placeholder=" "
              type="text"
              id="fullName"
              name="fullName"
              on:input={fullNameChanged}
              on:change={fullNameChanged}
              aria-describedby="error-container"
            />
            <span id="fullName-error" class="error-container"
              >{$_("register.form.error.empty_field")}</span
            >
          </div>

          <div>
            <label for="lang">{$_("register.form.lang")}</label>
            {#key $locale}
              <Svelecte
                class="svelecte-control svelecte-control2"
                options={langOptions}
                inputId="langs"
                clearable
                bind:readSelection={langSelection}
                bind:value={langValue}
                on:input={langChanged}
                on:change={langChanged}
                placeholder={$_("register.form.lang_placeholder")}
              />
            {/key}
            <span id="lang-error" class="error-container"
              >{$_("register.form.error.empty_field")}</span
            >
          </div>
        </div>

        <div class="form-row">
          <div class="input-wrapper">
            <label for="email">{$_("register.form.email")} *</label>
            <input
              placeholder=" "
              type="email"
              id="email"
              name="email"
              on:input={emailChanged}
              on:change={emailChanged}
              aria-describedby="error-container"
            />

            <span id="email-error" class="error-container"
              >{$_("register.form.error.invalid_email")}</span
            >
          </div>
          <div class="input-wrapper">
            <label for="phone">{$_("register.form.phone")} *</label>
            <input
              placeholder="+420602xxxxxx"
              type="tel"
              id="phone"
              name="phone"
              on:input={phoneChanged}
              on:change={phoneChanged}
              aria-describedby="error-container"
            />

            <span id="phone-error" class="error-container"
              >{$_("register.form.error.invalid_phone")}</span
            >
          </div>
        </div>

        <div class="form-row">
          <div style="margin-right: 0;">
            <p>
              <label for="intent">{$_("register.form.usage_intent")}:</label>
            </p>
            <textarea
              id="intent"
              name="intent"
              rows="4"
              cols="50"
              placeholder={$_("register.form.usage_intent_placeholder")}
            />
          </div>
        </div>

        <div class="agreement-wrapper">
          <div style="flex: 1; display: flex; flex-flow: column;">
            <div>
              <div style="display: flex; align-items: center;">
                <input
                  style="width: 1rem; margin-right: 0.5rem; height: 1rem;"
                  type="checkbox"
                  id="agreement"
                  name="agreement"
                  on:input={agreementChanged}
                  on:change={agreementChanged}
                />
                <label for="agreement" style="font-size: 1rem;"
                  >{$_("register.form.agree_check")}</label
                ><br />
              </div>
              <div style="display: flex; align-items: center; ">
                <p style="font-size: 0.8rem;">
                  {$_("register.form.terms_condition_text")}<a
                    href="https://www.kiwisignage.com/terms-and-conditions"
                    target="_blank">Terms & Conditions</a
                  >,
                  <a href="https://www.kiwisignage.com/eula" target="_blank"
                    >EULA</a
                  >
                  and
                  <a
                    href="https://www.kiwisignage.com/service-terms"
                    target="_blank">Service Terms</a
                  >.
                </p>
              </div>
            </div>
          </div>
          <div
            style="flex: 1; display: flex; justify-content: center; margin-top: 4rem; flex-flow: column; align-items: center;"
          >
            {#if !isFormSending}
              <button class="submit-button" type="submit"
                >{$_("register.form.create_registration")}</button
              >
            {:else}
              <button class="submit-button">
                <img src="./assets/loader.gif" alt="loader" />
              </button>
            {/if}
            <span id="form-error" class="error-container" />
          </div>
        </div>
      </form>
    </div>
  </TrialLayout>
{:else}
  <div class="thanks">
    <div class="thanks-header">
      <h1>{$_("register.thanks.header")}</h1>
      <h2>{$_("register.thanks.detail1")}</h2>
      <h4 style="margin-top: 1rem;">{$_("register.thanks.detail2")}</h4>
    </div>

    <Steps>
      <img
        class="step-check-img"
        slot="email-check"
        src="./assets/check-d7d7d7.png"
        alt="check-d7d7d7"
      />
      <img
        class="step-img"
        slot="email"
        src="./assets/email-c9c9c9.png"
        alt="email-c9c9c9"
      />

      <img
        class="step-check-img"
        slot="password-check"
        src="./assets/check-d7d7d7.png"
        alt="check-d7d7d7"
      />
      <img
        class="step-img"
        slot="password"
        src="./assets/password-c9c9c9.png"
        alt="password-c9c9c9"
      />

      <img
        class="step-check-img"
        slot="login-check"
        src="./assets/check-d7d7d7.png"
        alt="check-d7d7d7"
      />
      <img
        class="step-img"
        slot="login"
        src="./assets/login-c9c9c9.png"
        alt="login-c9c9c9"
      />
    </Steps>
  </div>
{/if}

<style lang="less" scoped>
  .input-wrapper {
    position: relative;
  }

  .error-container {
    display: none;
    text-align: left;
    margin-top: 0.5rem;

    font-size: 0.8rem;
    text-align: left;
    margin-top: 0.5rem;
    color: #fff;
    background-color: #ff5c4b;
    padding: 0.5rem;
  }



  .form-wrapper {
    background-color: #f5f5f5;
    padding: 1.875rem 2.5rem;
  }

  #register-form {
    text-align: center;
  }

  .form-row {
    display: flex;
    width: 100%;

    label {
      font-size: 16px;
      font-weight: 500;
      text-align: left;
      margin-top: 13px;
    }

    div {
      flex: 1;
    }

    div:first-child {
      margin-right: 1.875rem;
    }
  }

  .agreement-wrapper {
    margin-top: 1rem;
    display: flex;
    flex-flow: column;
    text-align: left;

    a {
      color: #6baa42;
      text-decoration: underline;
    }

    a:hover {
      text-decoration: none;
    }

    a:visited {
      color: #6baa42;
    }
  }

  input,
  textarea {
    width: 100%;
    padding: 12px 20px;
    margin-top: 13px;
    box-sizing: border-box;
    background-color: #fff;
  }

  textarea {
    resize: vertical;
  }

  input[type="checkbox"] {
    padding: 0;
    margin-top: 0;
    height: 2rem;
  }

  input[type="checkbox"]:valid {
    outline: none;
  }

  input:invalid:not(:focus):not(:placeholder-shown) {
    outline: 2px solid #ad0101;
  }

  input[type="checkbox"]:invalid {
    outline: none !important;
  }

  .submit-button {
    height: 3.25rem;

    img {
      height: 100%;
    }
  }

  .submit-button {
    font-size: 1rem;
    background-color: #6baa42;
    border: none;
    color: white;
    padding: 16px 32px;
    text-decoration: none;
    margin: 4px 2px;
    cursor: pointer;
    width: 16rem;
    text-transform: uppercase;
  }

  .submit-button:disabled {
    background-color: #c0c0c0;
  }

  .thanks {
    display: flex;
    flex-flow: column;
    align-items: center;
  }

  .thanks-header {
    white-space: pre-line;
    text-align: center;
    margin-bottom: 5rem;
    h1 {
      margin-bottom: 2rem;
    }
  }

  #form-error {
    text-align: center;
    width: 100%;
    font-size: 1rem;
  }

  @media only screen and (max-width: 600px) {
    .step-check-img {
      width: 2.3rem;
    }

    .step-img {
      width: 4rem;
    }


    input,
    textarea {
      padding: 12px 12px;
    }


    .form-wrapper {
      padding: 1.875rem 1.25rem;
    }

    .form-row {
      flex-flow: column;

      div:first-child {
        margin-right: 0;
      }
    }

    .agreement-wrapper {
      margin-top: 1rem;
    }

    .thanks-header {
      margin-bottom: 2.5rem;
      h1 {
        font-size: 1.8rem;
        margin-bottom: 1rem;
      }

      h2 {
        font-size: 1.2rem;
      }

      h4 {
        font-size: 0.8rem;
      }
    }
  }
</style>
